import { RolodexEntryJson } from 'types/rolodex/rolodexEntries'
import { parseArray, parseString } from 'utils/parsingHelpers'
import { GeckoEntryBase } from './geckoEntry'

export interface DataFreshnessConfig {
  enabled: boolean
  source: 'last_file_upload' | 'cypher_query'
  cypher_query?: string
}

export interface NavyProductionDashboardWidget {
  id: string
  title: string
  description?: string
  caption?: string
  type:
    | 'pie'
    | 'bar'
    | 'stacked_bar'
    | 'timeline'
    | 'line'
    | 'table'
    | 'filter'
    | 'graph'
    | 'kpi'
    | 'rich_text'
    | 'pfd'
    | 'progress'
    | 'side_drawer'
    | 'job_info'
    | 'job_risk'
  query?: string
  pfdDiagramId?: string
  pfdFilterParam?: string
  pfdMetadataKey?: string
  pfdNodeQuery?: string
  pfdNodeClickParam?: string
  pfdNodeClickField?: string
  filterType?:
    | 'select'
    | 'date'
    | 'number'
    | 'static'
    | 'date_period'
    | 'date_range'
    | 'toggle'
  toggleOnValue?: string
  toggleOffValue?: string
  toggleOnLabel?: string
  toggleOffLabel?: string
  geckoInternalOnly?: boolean
  parameterName?: string
  defaultValue?: string
  customQuery?: string
  dateRangeStartParam?: string
  dateRangeEndParam?: string
  valueKeys?: string[]
  tooltipKeys?: string[]
  categoryKey?: string
  width?: number
  height?: number
  segment?: string
  nodeSizeProp?: string
  edgeLengthProp?: string
  defaultLabelProp?: string
  kpiUnit?: string
  kpiSubtitle?: string
  kpiColor?: string
  showLegend?: boolean
  yAxisLabel?: string
  xAxisLabel?: string
  /** Query column whose distinct values are plotted as separate lines. */
  groupKey?: string
  /** Parameter name set when a bar category is clicked (value = category label). */
  drillDownParam?: string
  /** Identifier grouping this widget's drill-down children. */
  drillDownGroup?: string
  /** Links a widget to a parent's drillDownGroup so it appears on category click. */
  drillDownParent?: string
  disabledCategories?: string[]
  richTextContent?: string
  thresholdValue?: number
  thresholdLabel?: string
  tableStatusColumn?: string
  tableStatusGreen?: string
  tableStatusYellow?: string
  tableStatusRed?: string
  tableClickParam?: string
  tableClickColumn?: string
  tableProgressView?: boolean
  tableColumnColors?: Record<string, string>
  showCumulative?: boolean
  progressSuccessKey?: string
  progressTotalKey?: string
  historicalPerformanceKey?: string
  drawerPages?: NavyProductionDashboardPage[]
  drawerTriggerParam?: string
  headerRow?: boolean
  requiredParam?: string
}

export interface NavyProductionDashboardPage {
  id: string
  title: string
  widgets: NavyProductionDashboardWidget[]
}

interface NavyProductionDashboardConfigTags {
  organization: string
  site: string
}

/**
 * An entry that represents a Navy Production Dashboard configuration
 */
export class GeckoNavyProductionDashboardConfig extends GeckoEntryBase<
  {
    name: string
    pages: NavyProductionDashboardPage[]
    last_file_upload?: string
    data_freshness?: DataFreshnessConfig
  },
  NavyProductionDashboardConfigTags
> {
  private constructor(json: RolodexEntryJson) {
    const pages = parseArray<NavyProductionDashboardPage>(json.data.pages) ?? []
    const name = parseString(json.data.name) ?? ''

    const dataFreshness = json.data.data_freshness as
      | DataFreshnessConfig
      | undefined

    super(
      json,
      {
        name,
        pages,
        last_file_upload: parseString(json.data.last_file_upload),
        data_freshness: dataFreshness,
      },
      {
        organization: parseString(json.tags.organization) ?? '',
        site: parseString(json.tags.site) ?? '',
      },
      {}
    )
  }

  /**
   * Factory method to create a GeckoNavyProductionDashboardConfig from raw JSON
   * @param json The raw JSON to create an instance from
   */
  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoNavyProductionDashboardConfig(json)
  }
}
